import { useEffect, useRef, useMemo, memo, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { addBusinessDays, differenceInBusinessDays } from 'date-fns';
import { useTasks, useMilestones, useProject, useUpdateTask, useUpdateTaskStatus, useDependencies, useBatchUpdateTaskDates, useTeamMembers } from '@/hooks';
import { useProjectRisk } from '@/hooks/useProjectRisk';
import { useSprints } from '@/hooks/useSprints';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { buildDependencyGraph, shiftDependentTasks } from '@/utils/dependencyShifting';
import type { ShiftedTask } from '@/utils/dependencyShifting';
import { detectAllConflicts } from '@/utils/conflictDetection';
import type { TaskOverlap } from '@/utils/conflictDetection';
import { detectDateBreaches } from '@/utils/breachDetection';
import type { BreachTask, BreachProject, DateBreach } from '@/utils/breachDetection';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { StatusDropdown } from '@/components/features/tasks/StatusDropdown';
import { ZoomControls } from './ZoomControls';
import { SprintBoundaries } from './SprintBoundaries';
import { TaskDateModal } from './TaskDateModal';
import { CombinedWarningDialog } from './CombinedWarningDialog';
import { ZoomLevel, ZOOM_LEVEL_DAY_WIDTH, ZOOM_LEVEL_ORDER } from '@/types/timeline';
import { formatDateForInput, updateTaskDates, validateTaskDateIntegrity } from '@/utils/dateUtils';
import type { ZoomLevel as SprintZoomLevel } from '@/types/sprint';
import type { Database } from '@/types/database.types';
import type * as D3 from 'd3';

type Task = Database['public']['Tables']['tasks']['Row'];
type TaskType = Database['public']['Enums']['task_type'];
type MilestoneType = Database['public']['Tables']['milestones']['Row']['type'];

/**
 * Timeline data structure for D3 rendering
 */
interface TimelineTask {
    id: number;
    name: string;
    start: Date;
    end: Date;
    type: TaskType;
    status: Task['status'];
    /** True when this task is showing a drag-preview shift position (not persisted yet). */
    isShiftPreview?: boolean;
    /** True when this task is being moved (or shifted) and its new position creates a conflict. */
    isConflict?: boolean;
    /** True when this stationary task will be overlapped by a task being dragged. */
    isStaticConflict?: boolean;
    /** True when moving this task (or shifting it as a dependent) would breach the project committed date. */
    isBreachPreview?: boolean;
}

/** Task type → color per design-system.md. Single source of truth; legend derives from this. */
const TASK_COLOR_MAP: Record<TaskType, string> = {
    'design': '#9333EA',        // purple-600
    'development': '#2563EB',   // blue-600
    'integration': '#4F46E5',   // indigo-600
    'code_review': '#0891B2',   // cyan-600
    'qa': '#059669'             // emerald-600 (green)
};

const getTaskColor = (type: TaskType): string =>
    TASK_COLOR_MAP[type] || '#6B7280';

/**
 * Parse an ISO date string as local midnight.
 * Date-only strings ("YYYY-MM-DD") are spec-defined as UTC; appending
 * "T00:00:00" (no tz suffix) forces local-time interpretation.
 */
const parseLocalDate = (str: string): Date =>
    new Date(str.length === 10 ? str + 'T00:00:00' : str);

const getMilestoneColor = (type: MilestoneType): string => {
    const colorMap: Record<MilestoneType, string> = {
        'project_milestone': '#4F46E5',
        'external_dependency': '#F97316',
        'delivery_gate': '#059669'
    };
    return colorMap[type] || '#6B7280';
};

const getMilestoneTypeLabel = (type: MilestoneType): string => {
    const labelMap: Record<MilestoneType, string> = {
        'project_milestone': 'Project Milestone',
        'external_dependency': 'External Dependency',
        'delivery_gate': 'Delivery Gate'
    };
    return labelMap[type] || type;
};

const getTaskTypeLabel = (type: TaskType): string => {
    if (type === 'qa') return 'QA';
    return type.split('_').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
};

/** Outer margins used by D3 rendering. */
const MARGIN = { top: 60, right: 40, bottom: 60, left: 220 } as const;

const formatDate = (d: Date): string =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const getTickConfig = (zoom: ZoomLevel): d3.TimeInterval => {
    switch (zoom) {
        case ZoomLevel.Day:    return d3.timeDay.every(1)!;
        case ZoomLevel.Week:   return d3.timeWeek.every(1)!;
        case ZoomLevel.Sprint: return d3.timeWeek.every(2)!;
        case ZoomLevel.Month:  return d3.timeMonth.every(1)!;
    }
};

const getTickFormat = (zoom: ZoomLevel): ((d: Date) => string) => {
    switch (zoom) {
        case ZoomLevel.Day:    return d3.timeFormat('%b %d');
        case ZoomLevel.Week:   return d3.timeFormat('%b %d');
        case ZoomLevel.Sprint: return d3.timeFormat('%b %d');
        case ZoomLevel.Month:  return d3.timeFormat('%b %Y');
    }
};

const FIRST_DRAG_STORAGE_KEY = 'planarc-project-timeline-first-drag-seen';

interface ProjectTimelineProps {
    projectId: number;
}

interface D3RenderState {
    xScale: D3.ScaleTime<number, number>;
    innerHeight: number;
    timelineRange: { start: Date; end: Date };
    zoomLevel: SprintZoomLevel;
}

interface KeyboardEditState {
    taskId: number;
    taskName: string;
    currentStartDate: string;
    currentEndDate: string;
}

interface StatusEditState {
    taskId: number;
    taskName: string;
    currentStatus: Task['status'];
}

const STATUS_DOT_COLORS: Record<Task['status'], string> = {
    not_started: '#9CA3AF', // gray-400
    in_progress: '#3B82F6', // blue-500
    completed: '#22C55E',   // green-500
    blocked: '#EF4444',     // red-500
};

/**
 * ProjectTimeline - Renders project tasks as a horizontal timeline using D3.js.
 * Story 3.2: Adds drag-and-drop task movement with visual feedback, optimistic UI,
 * keyboard alternative, ARIA support, and touch support via d3.drag pointer events.
 */
const ProjectTimelineComponent = ({ projectId }: ProjectTimelineProps) => {
    const { data: tasks, isLoading: tasksLoading } = useTasks(projectId);
    const { data: project, isLoading: projectLoading } = useProject(projectId);
    const riskInfo = useProjectRisk(projectId);
    const { data: milestones = [], isLoading: milestonesLoading } = useMilestones(projectId);
    const { data: teamMembers = [], isLoading: teamMembersLoading } = useTeamMembers();
    const sprints = useSprints();
    const updateTaskMutation = useUpdateTask();
    const { data: rawDependencies = [] } = useDependencies(projectId);
    const batchUpdateMutation = useBatchUpdateTaskDates();

    const updateStatusMutation = useUpdateTaskStatus();
    const [statusEditState, setStatusEditState] = useState<StatusEditState | null>(null);
    const setStatusEditStateRef = useRef(setStatusEditState);
    setStatusEditStateRef.current = setStatusEditState;

    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const xScaleRef = useRef<d3.ScaleTime<number, number> | null>(null);
    const d3GroupRef = useRef<SVGGElement>(null);
    const isFirstRenderRef = useRef(true);

    // Zoom level persisted per project
    const [rawZoomLevel, setZoomLevel] = useLocalStorage<ZoomLevel>(
        `timeline-zoom-${projectId}`,
        ZoomLevel.Week
    );
    const zoomLevel = ZOOM_LEVEL_ORDER.includes(rawZoomLevel) ? rawZoomLevel : ZoomLevel.Week;

    const [scrollLeft, setScrollLeft] = useState(0);
    const [d3State, setD3State] = useState<D3RenderState | null>(null);

    // Optimistic task date overrides applied before the API response arrives
    const [optimisticUpdates, setOptimisticUpdates] = useState<Map<number, { start_date: string; end_date: string }>>(new Map());
    // Visual feedback: kept in a ref so changes don't trigger a full D3 effect re-run
    const taskVisualStateRef = useRef<Map<number, 'loading' | 'success' | 'error'>>(new Map());
    const visualStateTimeoutsRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
    // Generation counter per task — guards concurrent-drag cleanup from clobbering a later drag
    const dragGenerationRef = useRef<Map<number, number>>(new Map());
    // In-component toast notification (supports Undo for success, dismissal for error)
    const notificationTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const [taskNotification, setTaskNotification] = useState<{
        type: 'success' | 'error';
        taskName: string;
        dates?: string;
        shiftedCount?: number;
        undoFn?: () => void;
    } | null>(null);
    // ARIA live region text announced to screen readers during drag
    const [dragAnnouncement, setDragAnnouncement] = useState('');
    // Keyboard alternative: modal to shift dates by N business days
    const [keyboardEditState, setKeyboardEditState] = useState<KeyboardEditState | null>(null);
    // First-drag contextual hint
    const [showDragHint, setShowDragHint] = useState(false);
    // Dependent-task preview during drag: taskId → shifted dates
    const [dependentPreview, setDependentPreview] = useState<Map<number, { start_date: string; end_date: string }>>(new Map());
    // Conflict preview during drag: moved-task IDs whose new position creates a member overlap
    const [conflictPreview, setConflictPreview] = useState<Set<number>>(new Set());
    // Stationary tasks that will be overlapped by the dragged group (shown with red fill)
    const [staticConflictPreview, setStaticConflictPreview] = useState<Set<number>>(new Set());
    // Ref so D3 drag closures can read the latest conflictPreview without stale closure
    const conflictPreviewRef = useRef<Set<number>>(new Set());
    conflictPreviewRef.current = conflictPreview;
    // Breach preview: task IDs in the moved set when committed date will be breached (badge)
    const [breachPreview, setBreachPreview] = useState<Set<number>>(new Set());
    // Whether the committed date line should pulse red (breach detected during drag)
    const [breachPreviewActive, setBreachPreviewActive] = useState(false);
    // Tracks the last preview delta per dragged task to skip redundant BFS calls (P6)
    const lastPreviewDeltaRef = useRef<{ taskId: number; delta: number } | null>(null);
    // Tracks each primary task's previous drag's dependent IDs so stale optimistic overrides
    // can be cleaned up before the next drag on the same task (P8)
    const prevDependentIdsRef = useRef<Map<number, number[]>>(new Map());
    // Combined warning dialog state — holds both overlaps and breaches; promise resolve awaited in handleDragEnd
    const [warningDialogState, setWarningDialogState] = useState<{
        breaches: DateBreach[];
        conflicts: TaskOverlap[];
        resolve: (choice: 'proceed' | 'cancel' | 'auto-resolve') => void;
    } | null>(null);
    // Tracks the active dialog's resolve fn so it can be cancelled on unmount
    const conflictDialogResolveRef = useRef<((c: 'proceed' | 'cancel' | 'auto-resolve') => void) | null>(null);
    // Tracks whether component is still mounted; prevents state updates after unmount
    const isMountedRef = useRef(true);

    // Stable refs keep the latest callbacks visible inside D3 closures
    const handleDragEndRef = useRef<(taskId: number, daysDelta: number) => void>(() => {});
    const handleDragMoveRef = useRef<(taskId: number, daysDelta: number) => void>(() => {});
    const setKeyboardEditStateRef = useRef(setKeyboardEditState);
    setKeyboardEditStateRef.current = setKeyboardEditState;

    // Directly update a single task bar's visual state in D3 without triggering a full SVG rebuild
    const applyVisualStateToD3 = useCallback((taskId: number, vs: 'loading' | 'success' | 'error' | null) => {
        if (vs) {
            taskVisualStateRef.current.set(taskId, vs);
        } else {
            taskVisualStateRef.current.delete(taskId);
        }
        if (!d3GroupRef.current) return;
        d3.select(d3GroupRef.current)
            .selectAll<SVGGElement, TimelineTask>('.task-group')
            .filter(d => d.id === taskId)
            .select<SVGRectElement>('rect')
            .attr('class', vs === 'loading' ? 'task-bar task-bar-loading'
                          : vs === 'success' ? 'task-bar task-bar-success'
                          : vs === 'error'   ? 'task-bar task-bar-error'
                          : 'task-bar')
            .attr('fill', (d: TimelineTask) => {
                if (vs === 'success') return '#16a34a';
                if (vs === 'error') return '#dc2626';
                if (d.status === 'not_started') return '#D1D5DB';
                if (d.status === 'completed') return '#9CA3AF';
                return getTaskColor(d.type);
            })
            .attr('opacity', (d: TimelineTask) => {
                if (vs === 'loading') return 0.55;
                if (d.status === 'completed') return 0.6;
                if (d.status === 'not_started') return 0.75;
                return 1;
            });
    }, []);

    // Build the dependency graph from the fetched rows (memoised so D3 doesn't re-render on every render)
    const dependencyGraph = useMemo(
        () => buildDependencyGraph(rawDependencies),
        [rawDependencies]
    );

    // During drag: compute preview of dependent task positions for the current daysDelta
    const handleDragMove = useCallback((taskId: number, daysDelta: number) => {
        // P9: delta=0 means drag was cancelled/escaped — clear preview immediately without BFS.
        if (daysDelta === 0) {
            setDependentPreview(new Map());
            setConflictPreview(new Set());
            setStaticConflictPreview(new Set());
            setBreachPreview(new Set());
            setBreachPreviewActive(false);
            lastPreviewDeltaRef.current = null;
            return;
        }
        // P6: skip if delta hasn't changed since last call — avoids a full BFS on every pixel.
        const last = lastPreviewDeltaRef.current;
        if (last?.taskId === taskId && last?.delta === daysDelta) return;
        lastPreviewDeltaRef.current = { taskId, delta: daysDelta };

        const task = tasks?.find(t => t.id === taskId);
        if (!task || !tasks) {
            setDependentPreview(new Map());
            setConflictPreview(new Set());
            setStaticConflictPreview(new Set());
            setBreachPreview(new Set());
            setBreachPreviewActive(false);
            return;
        }

        const origStart = parseLocalDate(task.start_date);
        const origEnd = parseLocalDate(task.end_date);
        const shiftedStart = addBusinessDays(origStart, daysDelta);
        const { newStartDate: newStart, newEndDate: newEnd } = updateTaskDates(origStart, origEnd, shiftedStart);
        const tentativeTask = { ...task, start_date: formatDateForInput(newStart), end_date: formatDateForInput(newEnd) };

        const shiftedDependents = shiftDependentTasks(tentativeTask, tasks, dependencyGraph);
        const preview = new Map<number, { start_date: string; end_date: string }>();
        for (const s of shiftedDependents) {
            preview.set(s.id, { start_date: s.start_date, end_date: s.end_date });
        }
        setDependentPreview(preview);

        // Skip conflict detection while team member data is still loading (Fix 5)
        if (teamMembersLoading) {
            setConflictPreview(new Set());
            setStaticConflictPreview(new Set());
            return;
        }

        // Detect member assignment overlaps for the moved task + all shifted dependents.
        const movedTasks = [
            tentativeTask,
            ...shiftedDependents.map(s => {
                const orig = tasks.find(t => t.id === s.id);
                return orig ? { ...orig, start_date: s.start_date, end_date: s.end_date } : null;
            }).filter((t): t is NonNullable<typeof t> => t !== null),
        ];
        const overlaps = detectAllConflicts(movedTasks, tasks, teamMembers);
        // conflictPreview: moved task IDs that collide (shows ⚠ badge on dragged bar via D3)
        const conflictIds = new Set(overlaps.map(o => o.movedTask.id));
        setConflictPreview(conflictIds);
        // staticConflictPreview: stationary task IDs that will be overlapped (shows red fill)
        const staticIds = new Set(overlaps.map(o => o.conflictingTask.id));
        setStaticConflictPreview(staticIds);

        // Detect committed date breaches during drag for live visual feedback (AC9, AC11)
        if (project) {
            const breachProjectList: BreachProject[] = [project].filter(
                (p): p is typeof p & { committed_delivery_date: string } => !!p.committed_delivery_date,
            );
            const breachTaskList: BreachTask[] = movedTasks.map(t => ({
                id: t.id,
                name: t.name,
                start_date: t.start_date,
                end_date: t.end_date,
                project_id: (t as { project_id?: number | null }).project_id ?? null,
            }));
            const allBreachTasks: BreachTask[] = tasks.map(t => ({
                id: t.id,
                name: t.name,
                start_date: t.start_date,
                end_date: t.end_date,
                project_id: (t as { project_id?: number | null }).project_id ?? null,
            }));
            const breaches = detectDateBreaches(breachTaskList, breachProjectList, allBreachTasks);
            setBreachPreviewActive(breaches.length > 0);
            if (breaches.length > 0) {
                const breachingProjectIds = new Set(breaches.map(b => b.project.id));
                setBreachPreview(new Set(
                    breachTaskList
                        .filter(t => t.project_id !== null && breachingProjectIds.has(t.project_id))
                        .map(t => t.id),
                ));
            } else {
                setBreachPreview(new Set());
            }
        }
    }, [tasks, dependencyGraph, teamMembers, teamMembersLoading, project]);

    /**
     * Show the combined warning dialog for breaches and/or overlaps.
     * Cancels any previous orphaned dialog before opening a new one (Fix 6).
     * The resolve function is guarded to fire at most once (Fix 2).
     */
    const showWarningDialog = useCallback(
        (breaches: DateBreach[], conflicts: TaskOverlap[]): Promise<'proceed' | 'cancel' | 'auto-resolve'> =>
            new Promise((resolve) => {
                let consumed = false;
                const guardedResolve = (choice: 'proceed' | 'cancel' | 'auto-resolve') => {
                    if (consumed) return;
                    consumed = true;
                    conflictDialogResolveRef.current = null;
                    resolve(choice);
                };
                conflictDialogResolveRef.current = guardedResolve;
                // Cancel any pending dialog before overwriting state (Fix 6)
                setWarningDialogState(prev => {
                    if (prev) prev.resolve('cancel');
                    return { breaches, conflicts, resolve: guardedResolve };
                });
            }),
        [],
    );

    const handleDragEnd = useCallback(async (taskId: number, daysDelta: number) => {
        // Clear drag previews on drop regardless of outcome
        setDependentPreview(new Map());
        setConflictPreview(new Set());
        setStaticConflictPreview(new Set());
        setBreachPreview(new Set());
        setBreachPreviewActive(false);
        lastPreviewDeltaRef.current = null;

        if (daysDelta === 0) return;

        const task = tasks?.find(t => t.id === taskId);
        if (!task || !tasks) return;

        const origStart = parseLocalDate(task.start_date);
        const origEnd = parseLocalDate(task.end_date);

        if (!validateTaskDateIntegrity(origStart, origEnd)) return;

        const shiftedStart = addBusinessDays(origStart, daysDelta);
        const { newStartDate: newStart, newEndDate: newEnd } = updateTaskDates(origStart, origEnd, shiftedStart);

        const newStartStr = formatDateForInput(newStart);
        const newEndStr = formatDateForInput(newEnd);
        const origStartStr = formatDateForInput(origStart);
        const origEndStr = formatDateForInput(origEnd);

        // Compute dependent task shifts
        const movedTask = { id: taskId, start_date: newStartStr, end_date: newEndStr };
        const dependentShifts: ShiftedTask[] = shiftDependentTasks(movedTask, tasks, dependencyGraph);

        // Build the full set of tasks at their new positions (primary + dependents)
        const movedTasksForConflict = [
            { ...task, start_date: newStartStr, end_date: newEndStr },
            ...dependentShifts.map(s => {
                const orig = tasks.find(t => t.id === s.id);
                return orig ? { ...orig, start_date: s.start_date, end_date: s.end_date } : null;
            }).filter((t): t is NonNullable<typeof t> => t !== null),
        ];

        // Detect conflicts — skip if team member data is still loading (Fix 5)
        const allConflicts = teamMembersLoading
            ? []
            : detectAllConflicts(movedTasksForConflict, tasks, teamMembers);

        // Detect committed date breaches (AC1, AC6)
        const breachProjectList: BreachProject[] = project
            ? [project].filter((p): p is typeof p & { committed_delivery_date: string } => !!p.committed_delivery_date)
            : [];
        const breachTaskList: BreachTask[] = movedTasksForConflict.map(t => ({
            id: t.id,
            name: t.name,
            start_date: t.start_date,
            end_date: t.end_date,
            project_id: (t as { project_id?: number | null }).project_id ?? null,
        }));
        const allBreachTasks: BreachTask[] = tasks.map(t => ({
            id: t.id,
            name: t.name,
            start_date: t.start_date,
            end_date: t.end_date,
            project_id: (t as { project_id?: number | null }).project_id ?? null,
        }));
        const allBreaches = detectDateBreaches(breachTaskList, breachProjectList, allBreachTasks);

        let userChoice: 'proceed' | 'cancel' | 'auto-resolve' = 'proceed';
        if (allConflicts.length > 0 || allBreaches.length > 0) {
            userChoice = await showWarningDialog(allBreaches, allConflicts);
            if (!isMountedRef.current) return; // component unmounted while dialog was open (Fix 7)
            setWarningDialogState(null);
            if (userChoice === 'cancel') return;
        }

        // Compute auto-resolve task positions AFTER user confirmed auto-resolve (Fix 1 & 3 & 4)
        const autoResolveTasks: Array<{ id: number; start_date: string; end_date: string }> = [];
        const origAutoResolveDates: Array<{ id: number; start_date: string; end_date: string }> = [];
        if (userChoice === 'auto-resolve' && allConflicts.length > 0) {
            const latestEnd = movedTasksForConflict.reduce<Date>((max, t) => {
                const d = parseLocalDate(t.end_date);
                return d > max ? d : max;
            }, new Date(0));

            // Sequence conflicting tasks serially so they don't overlap each other (Fix 4)
            const conflictingIds = [...new Set(allConflicts.map(c => c.conflictingTask.id))];
            let currentEnd = latestEnd;
            for (const cid of conflictingIds) {
                const orig = tasks.find(t => t.id === cid);
                if (!orig) continue;
                const origS = parseLocalDate(orig.start_date);
                const origE = parseLocalDate(orig.end_date);
                // Use business-day duration so weekend-spanning tasks aren't stretched (Fix 3)
                const duration = Math.max(0, differenceInBusinessDays(origE, origS));
                const resolvedStart = addBusinessDays(currentEnd, 1);
                const resolvedEnd = addBusinessDays(resolvedStart, duration);
                currentEnd = resolvedEnd;
                origAutoResolveDates.push({ id: cid, start_date: orig.start_date, end_date: orig.end_date });
                autoResolveTasks.push({
                    id: cid,
                    start_date: formatDateForInput(resolvedStart),
                    end_date: formatDateForInput(resolvedEnd),
                });
            }
        }

        // Capture original dates of dependents before optimistic overrides are applied (P3)
        const origDependentDates = dependentShifts
            .map(s => { const orig = tasks.find(t => t.id === s.id); return orig ? { id: s.id, start_date: orig.start_date, end_date: orig.end_date } : null; })
            .filter((x): x is { id: number; start_date: string; end_date: string } => x !== null);

        // Bump generation so stale cleanup from a prior drag on this task won't interfere
        const generation = (dragGenerationRef.current.get(taskId) ?? 0) + 1;
        dragGenerationRef.current.set(taskId, generation);

        // P8: Clean up stale optimistic overrides from the previous drag on this same task
        const prevDepIds = prevDependentIdsRef.current.get(taskId) ?? [];
        if (prevDepIds.length > 0) {
            setOptimisticUpdates(prev => {
                const next = new Map(prev);
                for (const id of prevDepIds) next.delete(id);
                return next;
            });
        }
        prevDependentIdsRef.current.set(taskId, dependentShifts.map(s => s.id));

        // Optimistic updates for all affected tasks
        setOptimisticUpdates(prev => {
            const next = new Map(prev);
            next.set(taskId, { start_date: newStartStr, end_date: newEndStr });
            for (const s of dependentShifts) {
                next.set(s.id, { start_date: s.start_date, end_date: s.end_date });
            }
            for (const r of autoResolveTasks) {
                next.set(r.id, { start_date: r.start_date, end_date: r.end_date });
            }
            return next;
        });
        applyVisualStateToD3(taskId, 'loading');
        for (const s of dependentShifts) { applyVisualStateToD3(s.id, 'loading'); }
        for (const r of autoResolveTasks) { applyVisualStateToD3(r.id, 'loading'); }

        const batchPayload = [
            { id: taskId, start_date: newStartStr, end_date: newEndStr },
            ...dependentShifts,
            ...autoResolveTasks,
        ];

        const shiftMsg = dependentShifts.length > 0
            ? ` ${dependentShifts.length} dependent task${dependentShifts.length > 1 ? 's' : ''} also shifted.`
            : '';
        setDragAnnouncement(`Task moved to ${newStartStr} – ${newEndStr}.${shiftMsg}`);

        try {
            if (batchPayload.length > 1) {
                await batchUpdateMutation.mutateAsync(batchPayload);
            } else {
                await updateTaskMutation.mutateAsync({ id: taskId, updates: { start_date: newStartStr, end_date: newEndStr } });
            }
            if (!isMountedRef.current || dragGenerationRef.current.get(taskId) !== generation) return;

            applyVisualStateToD3(taskId, 'success');
            for (const s of dependentShifts) { applyVisualStateToD3(s.id, 'success'); }
            for (const r of autoResolveTasks) { applyVisualStateToD3(r.id, 'success'); }

            // Clear success state after flash; then let query cache become authoritative
            clearTimeout(visualStateTimeoutsRef.current.get(taskId));
            visualStateTimeoutsRef.current.set(taskId, setTimeout(() => {
                if (dragGenerationRef.current.get(taskId) === generation) {
                    applyVisualStateToD3(taskId, null);
                    for (const s of dependentShifts) { applyVisualStateToD3(s.id, null); }
                    for (const r of autoResolveTasks) { applyVisualStateToD3(r.id, null); }
                    setOptimisticUpdates(prev => {
                        const next = new Map(prev);
                        next.delete(taskId);
                        for (const s of dependentShifts) { next.delete(s.id); }
                        for (const r of autoResolveTasks) { next.delete(r.id); }
                        return next;
                    });
                }
            }, 1200));

            // Success toast with Undo (5 s) — undo also restores auto-resolved tasks (Fix 8)
            clearTimeout(notificationTimeoutRef.current);
            const capturedOrigAutoResolveDates = origAutoResolveDates;
            setTaskNotification({
                type: 'success',
                taskName: task.name,
                dates: `${newStartStr} – ${newEndStr}`,
                ...(dependentShifts.length > 0 && {
                    shiftedCount: dependentShifts.length,
                }),
                undoFn: async () => {
                    setTaskNotification(null);
                    const undoBatch = [
                        { id: taskId, start_date: origStartStr, end_date: origEndStr },
                        ...origDependentDates,
                        ...capturedOrigAutoResolveDates,
                    ];
                    if (undoBatch.length > 1) {
                        await batchUpdateMutation.mutateAsync(undoBatch);
                    } else {
                        await updateTaskMutation.mutateAsync({ id: taskId, updates: { start_date: origStartStr, end_date: origEndStr } });
                    }
                },
            });
            notificationTimeoutRef.current = setTimeout(() => setTaskNotification(null), 5000);

        } catch {
            if (!isMountedRef.current || dragGenerationRef.current.get(taskId) !== generation) return;

            setOptimisticUpdates(prev => {
                const next = new Map(prev);
                next.delete(taskId);
                for (const s of dependentShifts) { next.delete(s.id); }
                for (const r of autoResolveTasks) { next.delete(r.id); }
                return next;
            });
            applyVisualStateToD3(taskId, 'error');
            for (const s of dependentShifts) { applyVisualStateToD3(s.id, 'error'); }
            for (const r of autoResolveTasks) { applyVisualStateToD3(r.id, 'error'); }
            setDragAnnouncement(`Failed to save task move. Changes reverted.`);

            clearTimeout(visualStateTimeoutsRef.current.get(taskId));
            visualStateTimeoutsRef.current.set(taskId, setTimeout(() => {
                if (dragGenerationRef.current.get(taskId) === generation) {
                    applyVisualStateToD3(taskId, null);
                    for (const s of dependentShifts) { applyVisualStateToD3(s.id, null); }
                    for (const r of autoResolveTasks) { applyVisualStateToD3(r.id, null); }
                }
            }, 2000));

            clearTimeout(notificationTimeoutRef.current);
            setTaskNotification({ type: 'error', taskName: task.name });
            notificationTimeoutRef.current = setTimeout(() => setTaskNotification(null), 5000);
        }
    }, [tasks, updateTaskMutation, batchUpdateMutation, applyVisualStateToD3, dependencyGraph, teamMembers, teamMembersLoading, showWarningDialog, project]);

    // Keep refs in sync with latest callbacks every render
    handleDragEndRef.current = handleDragEnd;
    handleDragMoveRef.current = handleDragMove;

    // Clear timers and cancel any open dialog when the component unmounts (Fix 7)
    useEffect(() => {
        isMountedRef.current = true;
        // Capture the Map object at mount time; safe because .current is never reassigned
        const visualTimeouts = visualStateTimeoutsRef.current;
        return () => {
            isMountedRef.current = false;
            visualTimeouts.forEach(clearTimeout);
            clearTimeout(notificationTimeoutRef.current);
            // Resolve any pending warning dialog so the awaiting handleDragEnd doesn't hang
            conflictDialogResolveRef.current?.('cancel');
            conflictDialogResolveRef.current = null;
        };
    }, []);

    // AC9: Make committed delivery date line pulse red during drag when breach is detected
    useEffect(() => {
        if (!d3GroupRef.current) return;
        const line = d3.select(d3GroupRef.current).select<SVGLineElement>('.committed-date-line');
        if (line.empty()) return;
        if (breachPreviewActive) {
            line.attr('stroke', '#B91C1C')        // red-700 — more alarming during breach
                .attr('stroke-width', 4)
                .attr('stroke-dasharray', 'none') // solid during breach
                .attr('opacity', 1)
                .classed('animate-pulse', true);
        } else {
            line.attr('stroke', '#DC2626')        // red-600 — normal committed-date color
                .attr('stroke-width', 2.5)
                .attr('stroke-dasharray', '5,5')
                .attr('opacity', 0.8)
                .classed('animate-pulse', false);
        }
    }, [breachPreviewActive]);

    // Incorporate optimistic overrides and dependent-shift previews into the D3 dataset
    const timelineData = useMemo<TimelineTask[] | null>(() => {
        if (!tasks || tasks.length === 0) return null;

        const mapped = tasks
            .map(task => {
                const optimistic = optimisticUpdates.get(task.id);
                // During drag preview, show shifted position for dependent tasks
                const preview = !optimistic ? dependentPreview.get(task.id) : undefined;
                const startStr = optimistic?.start_date ?? preview?.start_date ?? task.start_date;
                const endStr = optimistic?.end_date ?? preview?.end_date ?? task.end_date;
                return {
                    id: task.id,
                    name: task.name,
                    start: parseLocalDate(startStr),
                    end: parseLocalDate(endStr),
                    type: task.type,
                    status: task.status,
                    isShiftPreview: !!preview && !optimistic,
                    isConflict: conflictPreview.has(task.id),
                    isStaticConflict: staticConflictPreview.has(task.id),
                    isBreachPreview: breachPreview.has(task.id),
                };
            })
            .filter(t => !isNaN(t.start.getTime()) && !isNaN(t.end.getTime()));

        return mapped.length > 0 ? mapped : null;
    }, [tasks, optimisticUpdates, dependentPreview, conflictPreview, staticConflictPreview, breachPreview]);

    const visibleDateRange = useMemo(() => {
        if (!d3State?.xScale || !containerRef.current) return undefined;
        const containerWidth = containerRef.current.clientWidth;
        const startDate = d3State.xScale.invert(scrollLeft);
        const endDate = d3State.xScale.invert(scrollLeft + containerWidth - MARGIN.left);
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return undefined;
        return `${formatDate(startDate)} – ${formatDate(endDate)}`;
    }, [scrollLeft, d3State]);

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        setScrollLeft(e.currentTarget.scrollLeft);
    }, []);

    const scrollToToday = useCallback(() => {
        if (!containerRef.current || !xScaleRef.current) return;
        const todayPx = xScaleRef.current(new Date());
        const containerWidth = containerRef.current.clientWidth;
        const targetScroll = todayPx + MARGIN.left - containerWidth / 2;
        containerRef.current.scrollTo({ left: Math.max(0, targetScroll), behavior: 'smooth' });
    }, []);

    const scrollByUnit = useCallback((direction: 'left' | 'right') => {
        if (!containerRef.current) return;
        containerRef.current.scrollBy({ left: direction === 'right' ? 200 : -200, behavior: 'smooth' });
    }, []);

    const scrollToEdge = useCallback((edge: 'start' | 'end') => {
        if (!containerRef.current) return;
        if (edge === 'start') {
            containerRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
            containerRef.current.scrollTo({ left: containerRef.current.scrollWidth, behavior: 'smooth' });
        }
    }, []);

    const zoomIn = useCallback(() => {
        const idx = ZOOM_LEVEL_ORDER.indexOf(zoomLevel);
        if (idx > 0) setZoomLevel(ZOOM_LEVEL_ORDER[idx - 1]!);
    }, [zoomLevel, setZoomLevel]);

    const zoomOut = useCallback(() => {
        const idx = ZOOM_LEVEL_ORDER.indexOf(zoomLevel);
        if (idx < ZOOM_LEVEL_ORDER.length - 1) setZoomLevel(ZOOM_LEVEL_ORDER[idx + 1]!);
    }, [zoomLevel, setZoomLevel]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!wrapperRef.current?.contains(e.target as Node)) return;
            const target = e.target as HTMLElement;
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return;

            switch (e.key) {
                case '+': case '=': e.preventDefault(); zoomIn(); break;
                case '-': e.preventDefault(); zoomOut(); break;
                case 't': case 'T': e.preventDefault(); scrollToToday(); break;
                case 'Home': e.preventDefault(); scrollToEdge('start'); break;
                case 'End': e.preventDefault(); scrollToEdge('end'); break;
                case 'ArrowLeft': if (e.ctrlKey || e.metaKey) { e.preventDefault(); scrollByUnit('left'); } break;
                case 'ArrowRight': if (e.ctrlKey || e.metaKey) { e.preventDefault(); scrollByUnit('right'); } break;
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [zoomIn, zoomOut, scrollToToday, scrollToEdge, scrollByUnit]);

    useEffect(() => {
        if (!svgRef.current || !containerRef.current || !d3GroupRef.current || !timelineData || timelineData.length === 0) return;

        const svg = d3.select(svgRef.current);
        const container = containerRef.current;

        const wasFirstRender = isFirstRenderRef.current;
        isFirstRenderRef.current = false;
        if (!wasFirstRender) {
            svg.style('transition', 'opacity 0.3s ease').style('opacity', '0');
        }

        const margin = MARGIN;
        const containerWidth = container.clientWidth;
        const barHeight = 32;
        const barPadding = 8;
        const innerHeight = timelineData.length * (barHeight + barPadding);
        const totalHeight = innerHeight + margin.top + margin.bottom;

        const minDate = d3.min(timelineData, d => d.start);
        const maxDate = d3.max(timelineData, d => d.end);
        if (!minDate || !maxDate) {
            d3.select(d3GroupRef.current).selectAll('*').remove();
            return;
        }
        const rawSpan = maxDate.getTime() - minDate.getTime();
        const datePadding = Math.max(rawSpan * 0.05, 24 * 60 * 60 * 1000);
        const paddedMinDate = new Date(minDate.getTime() - datePadding);
        const paddedMaxDate = new Date(maxDate.getTime() + datePadding);

        const dayWidth = ZOOM_LEVEL_DAY_WIDTH[zoomLevel];
        const totalDays = Math.ceil((paddedMaxDate.getTime() - paddedMinDate.getTime()) / (1000 * 60 * 60 * 24));
        const calculatedWidth = Math.max(containerWidth, totalDays * dayWidth);
        const width = calculatedWidth - margin.left - margin.right;

        svg
            .attr('width', calculatedWidth)
            .attr('height', totalHeight)
            .attr('class', 'timeline-svg');

        const g = d3.select(d3GroupRef.current);
        g.attr('transform', `translate(${margin.left},${margin.top})`);
        g.selectAll('*').remove();

        const xScale = d3.scaleTime()
            .domain([paddedMinDate, paddedMaxDate])
            .range([0, width])
            .nice();

        xScaleRef.current = xScale;

        const tickInterval = getTickConfig(zoomLevel);
        const tickFmt = getTickFormat(zoomLevel);

        // Grid lines
        const gridGroup = g.append('g').attr('class', 'grid');
        gridGroup.selectAll('.grid-line')
            .data(xScale.ticks(tickInterval))
            .enter()
            .append('line')
            .attr('class', 'grid-line')
            .attr('x1', d => xScale(d) ?? 0)
            .attr('x2', d => xScale(d) ?? 0)
            .attr('y1', 0)
            .attr('y2', innerHeight)
            .attr('stroke', '#E5E7EB')
            .attr('stroke-width', 1)
            .attr('stroke-dasharray', '2,2');

        // Today line
        const today = new Date();
        const [domainStart, domainEnd] = xScale.domain();
        if (domainStart && domainEnd && today >= domainStart && today <= domainEnd) {
            g.append('line')
                .attr('class', 'today-line')
                .attr('x1', xScale(today) ?? 0)
                .attr('x2', xScale(today) ?? 0)
                .attr('y1', -10)
                .attr('y2', innerHeight)
                .attr('stroke', '#DC2626')
                .attr('stroke-width', 2)
                .attr('stroke-dasharray', '4,4');
            g.append('text')
                .attr('x', xScale(today) ?? 0)
                .attr('y', -15)
                .attr('text-anchor', 'middle')
                .attr('fill', '#DC2626')
                .text('Today');
        }

        // X-axis
        g.append('g')
            .attr('class', 'x-axis')
            .attr('transform', `translate(0,${innerHeight})`)
            .call(d3.axisBottom(xScale)
                .ticks(tickInterval)
                .tickFormat(tickFmt as (d: Date | d3.NumberValue) => string))
            .selectAll('text')
            .attr('fill', '#374151');
        g.selectAll('.x-axis path, .x-axis line').attr('stroke', '#D1D5DB');

        // Drop indicator — a vertical dashed line that tracks the drop position during drag
        const dropIndicator = g.append('line')
            .attr('class', 'drag-drop-indicator')
            .attr('y1', -10)
            .attr('y2', innerHeight)
            .attr('stroke', '#2563EB')
            .attr('stroke-width', 2)
            .attr('stroke-dasharray', '4,4')
            .attr('pointer-events', 'none')
            .style('display', 'none');

        // Task groups
        const taskBars = g.selectAll<SVGGElement, TimelineTask>('.task-group')
            .data(timelineData)
            .enter()
            .append('g')
            .attr('class', 'task-group')
            .attr('transform', (_d, i) => `translate(0,${i * (barHeight + barPadding)})`);

        // Task labels — strikethrough for completed tasks
        taskBars.append('text')
            .attr('x', -10)
            .attr('y', barHeight / 2)
            .attr('dy', '0.35em')
            .attr('text-anchor', 'end')
            .attr('fill', d => d.status === 'completed' ? '#6B7280' : '#111827')
            .style('text-decoration', d => d.status === 'completed' ? 'line-through' : 'none')
            .text(d => d.name.length > 25 ? d.name.substring(0, 25) + '...' : d.name)
            .append('title')
            .text(d => d.name);

        // Task bar rects — opacity and fill reflect task status and active visual state (loading/success/error)
        taskBars.append('rect')
            .attr('class', d => {
                const vs = taskVisualStateRef.current.get(d.id);
                if (vs === 'loading') return 'task-bar task-bar-loading';
                if (vs === 'success') return 'task-bar task-bar-success';
                if (vs === 'error') return 'task-bar task-bar-error';
                if (d.isConflict) return 'task-bar task-bar-conflict';
                if (d.isStaticConflict) return 'task-bar task-bar-static-conflict animate-pulse';
                if (d.isShiftPreview) return 'task-bar task-bar-shift-preview';
                if (d.status === 'in_progress') return 'task-bar task-status-in-progress';
                return 'task-bar';
            })
            .attr('x', d => Math.min(xScale(d.start) ?? 0, xScale(d.end) ?? 0))
            .attr('y', 0)
            .attr('width', d => Math.max(4, Math.abs((xScale(d.end) ?? 0) - (xScale(d.start) ?? 0))))
            .attr('height', barHeight)
            .attr('rx', 4)
            .attr('fill', d => {
                const vs = taskVisualStateRef.current.get(d.id);
                if (vs === 'success') return '#16a34a'; // green-600
                if (vs === 'error') return '#dc2626';   // red-600
                if (d.isConflict) return '#FEF08A';     // yellow-200 per AC9
                if (d.isStaticConflict) return 'url(#conflict-stripe-red)'; // red diagonal-stripe per AC9
                if (d.isShiftPreview) return '#FFF8F0'; // amber-50 tint for shift preview
                if (d.status === 'not_started') return '#D1D5DB'; // gray-300 — not yet started
                if (d.status === 'completed') return '#9CA3AF';   // gray-400 — greyed out
                return getTaskColor(d.type);
            })
            .attr('stroke', d => {
                if (d.isConflict) return '#CA8A04';     // yellow-600 border for conflict
                if (d.isStaticConflict) return '#EF4444'; // red-500 border per AC9
                if (d.isShiftPreview) return '#F59E0B'; // amber-400 dashed border
                if (d.status === 'blocked') return '#DC2626'; // red-600 border for blocked
                return 'none';
            })
            .attr('stroke-width', d => {
                if (d.isConflict || d.isStaticConflict || d.isShiftPreview) return 2;
                if (d.status === 'blocked') return 2;
                return 0;
            })
            .attr('stroke-dasharray', d => (d.isShiftPreview && !d.isConflict) ? '4,3' : 'none')
            .attr('opacity', d => {
                const vs = taskVisualStateRef.current.get(d.id);
                if (vs === 'loading') return 0.55;
                if (d.isConflict) return 0.85;
                if (d.isStaticConflict) return 1; // animate-pulse CSS controls the pulsing
                if (d.isShiftPreview) return 0.6;
                if (d.status === 'completed') return 0.6;
                if (d.status === 'not_started') return 0.75;
                return 1;
            })
            .append('title')
            .text(d => {
                const start = d.start.toLocaleDateString();
                const end = d.end.toLocaleDateString();
                const conflictNote = d.isConflict ? '\n⚠ Conflict: member assignment overlap' : '';
                const previewNote = d.isShiftPreview ? '\n(Preview — will shift on drop)' : '';
                return `${d.name}\nType: ${getTaskTypeLabel(d.type)}\nDates: ${start} - ${end}\nStatus: ${d.status.replace(/_/g, ' ')}${conflictNote}${previewNote}`;
            });

        // Type indicator circle on bar
        taskBars.append('circle')
            .attr('cx', d => (xScale(d.start) ?? 0) + 8)
            .attr('cy', barHeight / 2)
            .attr('r', 4)
            .attr('fill', 'white')
            .attr('opacity', d => {
                const barW = Math.max(4, Math.abs((xScale(d.end) ?? 0) - (xScale(d.start) ?? 0)));
                return barW >= 20 ? 0.9 : 0;
            })
            .attr('pointer-events', 'none');

        // Status dot — clickable colored indicator at the right end of each bar.
        // Press S when the task is focused to open the same status picker via keyboard.
        const statusDots = taskBars.append('circle')
            .attr('class', 'task-status-dot')
            .attr('cx', d => Math.max((xScale(d.start) ?? 0) + 20, (xScale(d.end) ?? 0) - 8))
            .attr('cy', barHeight / 2)
            .attr('r', 4.5)
            .attr('fill', d => STATUS_DOT_COLORS[d.status])
            .attr('stroke', 'white')
            .attr('stroke-width', 1)
            .attr('cursor', 'pointer')
            .attr('opacity', d => {
                const barW = Math.max(4, Math.abs((xScale(d.end) ?? 0) - (xScale(d.start) ?? 0)));
                return barW >= 28 ? 0.95 : 0;
            })
            .attr('pointer-events', d => {
                const barW = Math.max(4, Math.abs((xScale(d.end) ?? 0) - (xScale(d.start) ?? 0)));
                return barW >= 28 ? 'all' : 'none';
            });

        statusDots.append('title')
            .text(d => `Status: ${d.status.replace(/_/g, ' ')}. Click to change (or press S when focused).`);

        statusDots
            .on('pointerdown.status', (event: PointerEvent) => event.stopPropagation())
            .on('click.status', (event: MouseEvent, d: TimelineTask) => {
                event.stopPropagation();
                setStatusEditStateRef.current({ taskId: d.id, taskName: d.name, currentStatus: d.status });
            });

        // Conflict warning badge (AC13) — amber ⚠ on dependent task bars that will
        // create a member assignment overlap at their shifted position.
        taskBars.filter(d => !!d.isConflict)
            .append('text')
            .attr('x', d => Math.max((xScale(d.start) ?? 0) + 2, (xScale(d.end) ?? 0) - 14))
            .attr('y', barHeight / 2)
            .attr('dy', '0.35em')
            .attr('fill', '#D97706') // amber-600
            .attr('font-size', '11px')
            .attr('pointer-events', 'none')
            .attr('aria-label', 'Shift conflict: member assignment overlap')
            .text('⚠');

        // Breach badge (AC11) — red 🚨 on moved task bars when a committed date will be breached.
        // Shown even when the task also has a conflict badge (both indicators are independent).
        taskBars.filter(d => !!d.isBreachPreview)
            .append('text')
            .attr('x', d => Math.max((xScale(d.start) ?? 0) + 2, (xScale(d.end) ?? 0) - 14))
            .attr('y', barHeight / 2)
            .attr('dy', '0.35em')
            .attr('fill', '#DC2626') // red-600
            .attr('font-size', '11px')
            .attr('pointer-events', 'none')
            .attr('aria-label', 'Committed date breach: project will be late')
            .text('🚨');

        // Committed delivery date and milestones
        const committedDeliveryDate = project?.committed_delivery_date
            ? parseLocalDate(project.committed_delivery_date)
            : null;
        const markerGroup = g.append('g').attr('class', 'markers-group');

        if (committedDeliveryDate) {
            const [ds, de] = xScale.domain();
            if (ds && de && committedDeliveryDate >= ds && committedDeliveryDate <= de) {
                markerGroup.append('line')
                    .attr('class', 'committed-date-line')
                    .attr('x1', xScale(committedDeliveryDate))
                    .attr('x2', xScale(committedDeliveryDate))
                    .attr('y1', -30)
                    .attr('y2', innerHeight)
                    .attr('stroke', '#DC2626')
                    .attr('stroke-width', 2.5)
                    .attr('stroke-dasharray', '5,5')
                    .attr('opacity', 0.8);
                markerGroup.append('text')
                    .attr('x', xScale(committedDeliveryDate))
                    .attr('y', -35)
                    .attr('text-anchor', 'middle')
                    .attr('fill', '#DC2626')
                    .text('Committed Delivery')
                    .append('title')
                    .text(`Committed Delivery: ${committedDeliveryDate.toLocaleDateString()}`);
            }
        }

        // Estimated completion marker + risk zone when project is at risk
        if (riskInfo?.isAtRisk && riskInfo.estimatedCompletion && committedDeliveryDate) {
            const estDate = riskInfo.estimatedCompletion;
            const [ds, de] = xScale.domain();
            const committedInView = ds && de && committedDeliveryDate >= ds && committedDeliveryDate <= de;
            const estInView = ds && de && estDate >= ds && estDate <= de;

            // Risk zone between committed and estimated dates
            if (committedInView || estInView) {
                const zoneStart = xScale(committedDeliveryDate);
                const zoneEndRaw = estInView ? xScale(estDate) : (de ? Math.max(xScale(de), zoneStart + 4) : zoneStart + 40);
                const zoneEnd = Math.max(zoneEndRaw, zoneStart + 4);
                if (zoneEnd > zoneStart) {
                    markerGroup.append('rect')
                        .attr('x', zoneStart)
                        .attr('y', 0)
                        .attr('width', zoneEnd - zoneStart)
                        .attr('height', innerHeight)
                        .attr('fill', 'rgba(220,38,38,0.07)')
                        .attr('pointer-events', 'none')
                        .append('title')
                        .text('Risk zone: project is estimated to complete after committed date');
                }
            }

            // Estimated completion marker line
            if (estInView) {
                markerGroup.append('line')
                    .attr('x1', xScale(estDate))
                    .attr('x2', xScale(estDate))
                    .attr('y1', -30)
                    .attr('y2', innerHeight)
                    .attr('stroke', '#f97316')
                    .attr('stroke-width', 2)
                    .attr('stroke-dasharray', '4,4')
                    .attr('opacity', 0.85);
                markerGroup.append('text')
                    .attr('x', xScale(estDate))
                    .attr('y', -35)
                    .attr('text-anchor', 'middle')
                    .attr('fill', '#f97316')
                    .attr('font-size', '11px')
                    .text('Est. Completion')
                    .append('title')
                    .text(`Estimated Completion: ${estDate.toLocaleDateString()} (${riskInfo.daysDifference} working days late)`);
            }
        }

        milestones.forEach((milestone) => {
            const milestoneDate = parseLocalDate(milestone.target_date);
            const [ds, de] = xScale.domain();
            if (ds && de && milestoneDate >= ds && milestoneDate <= de) {
                const markerColor = getMilestoneColor(milestone.type);
                markerGroup.append('line')
                    .attr('x1', xScale(milestoneDate))
                    .attr('x2', xScale(milestoneDate))
                    .attr('y1', 0)
                    .attr('y2', innerHeight)
                    .attr('stroke', markerColor)
                    .attr('stroke-width', 2)
                    .attr('opacity', 0.8);
                const diamondSize = 8;
                markerGroup.append('path')
                    .attr('d', `M ${xScale(milestoneDate)},${-diamondSize} L ${xScale(milestoneDate) + diamondSize},0 L ${xScale(milestoneDate)},${diamondSize} L ${xScale(milestoneDate) - diamondSize},0 Z`)
                    .attr('fill', markerColor)
                    .attr('opacity', 0.9);
                markerGroup.append('text')
                    .attr('x', xScale(milestoneDate))
                    .attr('y', -25)
                    .attr('text-anchor', 'middle')
                    .attr('fill', markerColor)
                    .text(milestone.name.length > 15 ? milestone.name.substring(0, 15) + '...' : milestone.name)
                    .append('title')
                    .text(`${milestone.name}\n${milestoneDate.toLocaleDateString()}\nType: ${getMilestoneTypeLabel(milestone.type)}${milestone.description != null ? '\n' + milestone.description : ''}`);
            }
        });

        // ── Drag-and-drop behaviour ───────────────────────────────────────────
        // Applied per task-group element so each has independent drag state.
        taskBars.each(function(taskData, i) {
            const rowY = i * (barHeight + barPadding);
            const originalTransform = `translate(0,${rowY})`;
            const group = d3.select<SVGGElement, TimelineTask>(this);

            // Make focusable for keyboard users (AC20)
            group
                .attr('tabindex', '0')
                .attr('role', 'button')
                .attr('aria-label',
                    `Task: ${taskData.name}, ${taskData.start.toLocaleDateString()} to ${taskData.end.toLocaleDateString()}. Press Enter to move with keyboard.`)
                .style('cursor', 'grab')
                .style('outline', 'none');

            // Enter / Space opens the keyboard-date modal (AC20); S opens the status picker
            group.on('keydown.taskdrag', function(event: KeyboardEvent) {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setKeyboardEditStateRef.current({
                        taskId: taskData.id,
                        taskName: taskData.name,
                        currentStartDate: formatDateForInput(taskData.start),
                        currentEndDate: formatDateForInput(taskData.end),
                    });
                } else if (event.key === 's' || event.key === 'S') {
                    event.preventDefault();
                    setStatusEditStateRef.current({
                        taskId: taskData.id,
                        taskName: taskData.name,
                        currentStatus: taskData.status,
                    });
                }
            });

            let cumulativeDx = 0;
            let dragActive = false;
            let dragCancelled = false;
            let escapeCleanup = () => {};

            const drag = d3.drag<SVGGElement, TimelineTask>()
                .on('start', function() {
                    cumulativeDx = 0;
                    dragActive = false;
                    dragCancelled = false;

                    // Register Escape key handler to cancel the drag (AC17)
                    const onEscape = (e: KeyboardEvent) => {
                        if (e.key === 'Escape' && dragActive) {
                            e.preventDefault();
                            dragCancelled = true;
                            d3.select(this)
                                .attr('transform', originalTransform)
                                .style('cursor', 'grab')
                                .style('opacity', null)
                                .style('filter', null);
                            dropIndicator.style('display', 'none');
                            setDragAnnouncement('Drag cancelled. Task returned to original position.');
                        }
                    };
                    document.addEventListener('keydown', onEscape);
                    escapeCleanup = () => document.removeEventListener('keydown', onEscape);
                })
                .on('drag', function(event) {
                    if (dragCancelled) return;

                    cumulativeDx += (event as D3.D3DragEvent<SVGGElement, TimelineTask, TimelineTask>).dx;

                    // Activation threshold of 5px prevents accidental drags on clicks
                    if (!dragActive && Math.abs(cumulativeDx) >= 5) {
                        dragActive = true;
                        d3.select(this).raise();
                        setDragAnnouncement(`Dragging task: ${taskData.name}`);

                        // Show first-drag hint once
                        if (!localStorage.getItem(FIRST_DRAG_STORAGE_KEY)) {
                            localStorage.setItem(FIRST_DRAG_STORAGE_KEY, 'true');
                            setShowDragHint(true);
                        }
                    }

                    if (!dragActive) return;

                    // Visual drag lift (AC6)
                    d3.select(this)
                        .style('opacity', '0.7')
                        .style('filter', 'drop-shadow(0 4px 8px rgba(0,0,0,0.25))')
                        .style('cursor', 'grabbing')
                        .attr('transform', `translate(${cumulativeDx},${rowY})`);

                    // Update drop indicator (AC13)
                    const newStartX = (xScale(taskData.start) ?? 0) + cumulativeDx;
                    dropIndicator
                        .attr('x1', newStartX)
                        .attr('x2', newStartX)
                        .style('display', null);

                    // Preview dependent task positions (AC5, AC10)
                    const previewDelta = Math.round(cumulativeDx / dayWidth);
                    handleDragMoveRef.current(taskData.id, previewDelta);
                })
                .on('end', function() {
                    escapeCleanup();

                    if (dragCancelled || !dragActive) {
                        // Reset visual state without a date change
                        d3.select(this)
                            .attr('transform', originalTransform)
                            .style('cursor', 'grab')
                            .style('opacity', null)
                            .style('filter', null);
                        dropIndicator.style('display', 'none');
                        handleDragMoveRef.current(taskData.id, 0); // clear preview
                        return;
                    }

                    // Reset visual; optimistic update will re-render with correct position
                    d3.select(this)
                        .attr('transform', originalTransform)
                        .style('cursor', 'grab')
                        .style('opacity', null)
                        .style('filter', null);
                    dropIndicator.style('display', 'none');

                    const daysDelta = Math.round(cumulativeDx / dayWidth);
                    handleDragEndRef.current(taskData.id, daysDelta);
                });

            group.call(drag);
        });
        // ── End drag-and-drop ─────────────────────────────────────────────────

        if (!wasFirstRender) {
            svg.transition().duration(300).style('opacity', '1');
        }

        setD3State({
            xScale,
            innerHeight,
            timelineRange: { start: paddedMinDate, end: paddedMaxDate },
            zoomLevel: zoomLevel.toLowerCase() as SprintZoomLevel,
        });

        return () => { svg.interrupt(); };
    }, [timelineData, project, milestones, zoomLevel, riskInfo]);

    const handleKeyboardConfirm = useCallback((daysDelta: number) => {
        const state = keyboardEditState;
        if (!state) return;
        handleDragEndRef.current(state.taskId, daysDelta);
    }, [keyboardEditState]);

    const handleDismissDragHint = useCallback(() => setShowDragHint(false), []);

    if (tasksLoading || projectLoading || milestonesLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <Spinner size="lg" />
                    <p className="mt-4 text-gray-600">Loading timeline...</p>
                </div>
            </div>
        );
    }

    if (!tasks || tasks.length === 0) {
        return (
            <EmptyState
                title="No tasks yet"
                description="Create tasks for this project to see them visualized on the timeline."
            />
        );
    }

    return (
        <div ref={wrapperRef} className="timeline-container">
            {/* ARIA live region — announces drag state changes to screen readers (AC11, AC21) */}
            <div
                role="status"
                aria-live="polite"
                aria-atomic="true"
                className="sr-only"
            >
                {dragAnnouncement}
            </div>

            {/* Task move notification toast (AC9 success, AC10 error) */}
            {taskNotification && (
                <div
                    role="alert"
                    aria-live="assertive"
                    className={`mx-6 mt-3 flex items-center justify-between rounded-md border px-4 py-2.5 text-sm ${
                        taskNotification.type === 'success'
                            ? 'bg-green-50 border-green-200 text-green-800'
                            : 'bg-red-50 border-red-200 text-red-800'
                    }`}
                >
                    <span className="flex items-center gap-2">
                        {taskNotification.type === 'success' ? '✓' : '✕'}
                        {taskNotification.type === 'success'
                            ? <>
                                {`"${taskNotification.taskName}" moved to ${taskNotification.dates}`}
                                {taskNotification.shiftedCount != null && taskNotification.shiftedCount > 0 &&
                                    <span className="ml-1 text-green-700">
                                        {` · ${taskNotification.shiftedCount} dependent task${taskNotification.shiftedCount > 1 ? 's' : ''} shifted`}
                                    </span>
                                }
                              </>
                            : `Failed to move "${taskNotification.taskName}" — changes reverted`}
                    </span>
                    <span className="flex items-center gap-3 ml-4">
                        {taskNotification.type === 'success' && taskNotification.undoFn && (
                            <button
                                type="button"
                                onClick={taskNotification.undoFn}
                                className="font-medium underline hover:no-underline"
                            >
                                Undo
                            </button>
                        )}
                        <button
                            type="button"
                            aria-label="Dismiss"
                            onClick={() => setTaskNotification(null)}
                            className="font-medium opacity-60 hover:opacity-100"
                        >
                            ✕
                        </button>
                    </span>
                </div>
            )}

            {/* First-drag contextual hint (AC19) */}
            {showDragHint && (
                <div
                    role="status"
                    className="mx-6 mt-4 flex items-center justify-between rounded-md bg-blue-50 border border-blue-200 px-4 py-2 text-sm text-blue-800"
                >
                    <span>💡 Tip: Drag tasks left or right to reschedule them. Press Escape to cancel.</span>
                    <button
                        type="button"
                        aria-label="Dismiss tip"
                        onClick={handleDismissDragHint}
                        className="ml-4 text-blue-600 hover:text-blue-800 font-medium"
                    >
                        ✕
                    </button>
                </div>
            )}

            {/* Keyboard edit modal (AC20) */}
            {keyboardEditState && (
                <TaskDateModal
                    taskName={keyboardEditState.taskName}
                    currentStartDate={keyboardEditState.currentStartDate}
                    currentEndDate={keyboardEditState.currentEndDate}
                    onConfirm={handleKeyboardConfirm}
                    onClose={() => setKeyboardEditState(null)}
                />
            )}

            {/* Status picker modal — triggered by clicking the status dot or pressing S on a focused task */}
            {statusEditState && (
                <Modal
                    isOpen={true}
                    onClose={() => setStatusEditState(null)}
                    title={`Change Status: ${statusEditState.taskName}`}
                    size="sm"
                >
                    <div className="flex flex-col items-start gap-4 py-2">
                        <p className="text-sm text-gray-600">Select a new status for this task:</p>
                        <StatusDropdown
                            currentStatus={statusEditState.currentStatus}
                            onStatusChange={(newStatus) => {
                                updateStatusMutation.mutate({
                                    taskId: statusEditState.taskId,
                                    status: newStatus,
                                    projectId,
                                });
                                setStatusEditState(null);
                            }}
                        />
                    </div>
                </Modal>
            )}

            {/* Combined warning dialog — shows overlaps and/or committed-date breaches (AC2–AC5, AC8–AC11) */}
            {warningDialogState && (
                <CombinedWarningDialog
                    breaches={warningDialogState.breaches}
                    overlaps={warningDialogState.conflicts}
                    onCancel={() => warningDialogState.resolve('cancel')}
                    onProceed={() => warningDialogState.resolve('proceed')}
                    onAutoResolve={() => warningDialogState.resolve('auto-resolve')}
                />
            )}

            <div className="px-6 pt-4">
                <ZoomControls
                    currentZoom={zoomLevel}
                    onZoomChange={setZoomLevel}
                    onTodayClick={scrollToToday}
                    visibleDateRange={visibleDateRange}
                />
            </div>

            <div
                ref={containerRef}
                className="overflow-x-auto bg-white rounded-lg shadow p-6"
                style={{ scrollBehavior: 'smooth' }}
                onScroll={handleScroll}
                role="region"
                aria-label="Project timeline visualization"
            >
                <svg
                    ref={svgRef}
                    className="timeline-svg"
                    role="img"
                    aria-label={`Timeline showing ${timelineData?.length ?? 0} tasks`}
                >
                    <defs>
                        {/* Diagonal red stripe pattern for stationary tasks that will be overlapped (AC9) */}
                        <pattern id="conflict-stripe-red" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(-45)">
                            <rect width="8" height="8" fill="#FEE2E2" />
                            <rect width="4" height="8" fill="#EF4444" opacity="0.55" />
                        </pattern>
                    </defs>
                    <title>{`Timeline showing ${timelineData?.length ?? 0} tasks`}</title>
                    {d3State && (
                        <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
                            <SprintBoundaries
                                xScale={d3State.xScale}
                                height={d3State.innerHeight}
                                sprints={sprints}
                                timelineRange={d3State.timelineRange}
                                zoomLevel={d3State.zoomLevel}
                            />
                        </g>
                    )}
                    <g ref={d3GroupRef} />
                </svg>
            </div>

            {/* Legend */}
            <div className="mt-6 bg-white rounded-lg shadow p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Task Types</h3>
                <div className="flex flex-wrap gap-4 text-sm">
                    {(Object.keys(TASK_COLOR_MAP) as TaskType[]).map(type => (
                        <div key={type} className="flex items-center gap-2">
                            <div
                                className="w-4 h-4 rounded"
                                style={{ backgroundColor: getTaskColor(type) }}
                                aria-hidden="true"
                            />
                            <span className="text-gray-700">{getTaskTypeLabel(type)}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export const ProjectTimeline = memo(ProjectTimelineComponent);
